import {
  MutateFunction,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';

import { queryKey } from '~teacher/hook/activity/queryKey';
import { QuestionScore } from '~teacher/models/activities';

import {
  CreateQuestionScoresResponse,
  createQuestionScores,
} from '../../api/questionScores';
import { ActivityResponse } from './activity';

export const addQuestionScore =
  (questionScore: Omit<QuestionScore, 'studentScores'>) =>
  (activity: ActivityResponse) =>
    produce(activity, (nextActivity) => {
      if (!nextActivity.activity.questionScores) {
        nextActivity.activity.questionScores = [];
      }
      nextActivity.activity.questionScores.push(questionScore.questionId);
      nextActivity.questionScores[questionScore.questionId] = {
        ...questionScore,
        studentScores: {},
      };
    });

export function addToQuestionScoreCache({
  queryClient,
  activityInstanceId,
  questionScore,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  questionScore: Omit<QuestionScore, 'studentScores'>;
}) {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    addQuestionScore(questionScore)
  );
}

export function useCreateQuestionScore({
  activityInstanceId,
  questionId,
}: {
  activityInstanceId: number;
  questionId: number;
}) {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      gradingWeight = 1,
      ungradedScore = 0,
    }: CreateQuestionMutationParameters) =>
      createQuestionScores({
        activityInstanceId: activityInstanceId,
        questionId: questionId,
        gradingWeight: gradingWeight,
        ungradedScore: ungradedScore,
      }),
    {
      onSuccess: (questionScore) => {
        addToQuestionScoreCache({
          queryClient: queryClient,
          activityInstanceId: activityInstanceId,
          questionScore: {
            ...questionScore,
            updatedDate: new Date(questionScore.updatedDate),
          },
        });
      },
    }
  );
}

export type CreateQuestionMutationParameters = {
  gradingWeight?: number;
  ungradedScore?: number;
};

export type CreateQuestionMutateFunction = MutateFunction<
  CreateQuestionScoresResponse,
  unknown,
  CreateQuestionMutationParameters,
  unknown
>;
